import React, { useContext } from "react";
import { NavLink } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { userRoles } from "../config";
import {
  SafetyOutlined,
  UserAddOutlined,
  MailOutlined,
  ClockCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { ReactComponent as CustomFieldIcon } from "../assets/svg/lead-custom-field.svg";

const SettingsPage = () => {
  const { user, isLight, isEmailSetupView, isWebToLeadView, isLeadCustomFieldView } = useContext(AuthContext);

  const menuItems = [
    {
      key: "user-management",
      path: "/settings/user-management",
      title: "User Management & Access Control",
      description: "Manage system users, invite team members, set up hierarchal reporting structures, and configure role-based access control permissions.",
      icon: <SafetyOutlined className="tw-text-xl" />,
      isVisible: user?.role === userRoles?.admin,
      color: "#5d44ff",
    },
    {
      key: "web-to-lead",
      path: "/settings/web-to-lead",
      title: "Web to Leads Setup",
      description: "Generate embedded HTML or widget forms, map custom lead capture configurations, and generate standard contact capture links.",
      icon: <UserAddOutlined className="tw-text-xl" />,
      isVisible: (user?.role !== userRoles?.admin && isWebToLeadView) || user?.role === userRoles?.admin,
      color: "#10b981",
    },
    {
      key: "lead-custom-fields",
      path: "/settings/lead-custom-fields",
      title: "Lead Custom Fields",
      description: "Create and manage custom custom data fields for leads. Customize forms, drop-downs, checklists, and inputs for capturing lead details.",
      icon: <CustomFieldIcon className="tw-w-[20px] tw-h-[20px] tw-inline-block" />,
      isVisible: (user?.role !== userRoles?.admin && isLeadCustomFieldView) || user?.role === userRoles?.admin,
      color: "#f59e0b",
    },
    {
      key: "email-setup",
      path: "/settings/email-setup",
      title: "Email Campaigns Setup",
      description: "Configure SMTP outgoing mail servers, authenticate email sending domains, and set up tracking configurations for campaigns.",
      icon: <MailOutlined className="tw-text-xl" />,
      isVisible: (user?.role !== userRoles?.admin && isEmailSetupView) || user?.role === userRoles?.admin,
      color: "#3b82f6",
    },
    {
      key: "availability",
      path: "/leads-timeline/availability",
      title: "Calendar & Availability Settings",
      description: "Set up booking links, define active meeting hours, manage timezone rules, connect Google/Outlook calendar events, and custom notifications.",
      icon: <ClockCircleOutlined className="tw-text-xl" />,
      isVisible: true,
      color: "#ec4899",
    },
  ];

  const visibleMenuItems = menuItems.filter((item) => item.isVisible);

  return (
    <section className="tw-py-8 tw-px-4 md:tw-px-8 tw-min-h-[calc(100vh-64px)]">
      <div className="tw-max-w-6xl tw-mx-auto">
        {/* Header */}
        <div className="tw-mb-8">
          <h1 className={`tw-text-3xl tw-font-bold tw-tracking-tight tw-mb-2 ${
            isLight ? "tw-text-slate-900" : "tw-text-white"
          }`}>
            Settings Portal
          </h1>
          <p className={`tw-text-sm ${
            isLight ? "tw-text-slate-500" : "tw-text-gray-400"
          }`}>
            Configure your workspace, customize forms, connect calendars, and set up your system preferences.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
          {visibleMenuItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              className={`tw-group tw-relative tw-overflow-hidden tw-flex tw-flex-col tw-justify-between tw-p-7 tw-rounded-2xl tw-border tw-transition-all tw-duration-300 hover:tw-translate-y-[-4px] ${
                isLight
                  ? "tw-bg-white tw-border-slate-100 tw-shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:tw-border-[#5d44ff]/40 hover:tw-shadow-[0_18px_35px_-8px_rgba(93,68,255,0.15)]"
                  : "tw-bg-[#1f2937]/90 tw-border-gray-700/80 tw-shadow-[0_8px_30px_rgba(0,0,0,0.35)] hover:tw-border-[#818cf8]/50 hover:tw-shadow-[0_18px_35px_-8px_rgba(129,140,248,0.2)]"
              }`}
            >
              <div>
                {/* Icon Container */}
                <div
                  className="tw-w-12 tw-h-12 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mb-5 tw-transition-colors tw-shadow-sm"
                  style={{
                    backgroundColor: isLight ? `${item.color}15` : `${item.color}25`,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </div>

                {/* Title */}
                <h3 className={`tw-text-lg tw-font-bold tw-mb-2.5 tw-transition-colors ${
                  isLight
                    ? "tw-text-slate-800 group-hover:tw-text-[#5d44ff]"
                    : "tw-text-white group-hover:tw-text-[#818cf8]"
                }`}>
                  {item.title}
                </h3>

                {/* Description */}
                <p className={`tw-text-sm tw-leading-relaxed tw-mb-6 ${
                  isLight ? "tw-text-slate-500" : "tw-text-gray-400"
                }`}>
                  {item.description}
                </p>
              </div>

              {/* Bottom Action Link */}
              <div className={`tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-mt-auto tw-z-10 ${
                isLight ? "tw-text-[#5d44ff]" : "tw-text-[#818cf8]"
              }`}>
                <span>Configure</span>
                <RightOutlined className="tw-text-xs tw-transition-transform tw-duration-200 group-hover:tw-translate-x-1" />
              </div>
            </NavLink>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SettingsPage;
